/**
 * AccessDeniedHandler.jsx
 * CCH Care — Phase 2 (Access Control)
 * Last Modified: 2026-05-13
 *
 * Global listener for 403 responses from the API client. Mount once
 * inside AppShell (or any component beneath the router + auth provider).
 *
 * Behavior:
 *   - On 'care:access-denied' DOM event (fired by client.js for any
 *     403 response without { meta: { silentOn403: true } }):
 *       1. Push a notification-shaped toast through useNotifications
 *          (passed in as `onToast`). Shape matches ToastContainer's
 *          existing render path for server-driven notifications.
 *       2. Navigate to /dashboard (unless already there) so the user
 *          lands on a safe page instead of being stuck on an empty
 *          state from the rejected request.
 *
 * Toast messages are derived from the audit-style `code` when present,
 * falling back to the raw error message. Codes match canonical audit
 * actions from functions.php:
 *   - CARE_PATIENT_SCOPE_DENIED → out-of-scope patient
 *   - CARE_PERMISSION_DENIED    → missing permission (e.g. view_all)
 *   - CARE_TENANT_SCOPE_DENIED  → cross-tenant attempt (rare)
 *
 * Renders null. No visual output of its own — the toast container
 * already mounted in AppShell handles rendering.
 *
 * Wiring (in AppShell):
 *   const notifications = useNotifications();
 *   <AccessDeniedHandler onToast={notifications.addToast} />
 */
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function AccessDeniedHandler({ onToast }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    function handleAccessDenied(e) {
      const { message, code, requestUrl, requestMethod } = e.detail || {};

      // Translate audit codes to user-facing copy.
      // Falls back to the raw API message when no code is present.
      const friendlyMessage = (() => {
        if (code === 'CARE_PATIENT_SCOPE_DENIED' ||
            (typeof message === 'string' && /patient.*scope/i.test(message))) {
          return "That resident isn't in your shift assignments.";
        }
        if (code === 'CARE_PERMISSION_DENIED' ||
            (typeof message === 'string' && /permission|view_all/i.test(message))) {
          return "You don't have permission to access that.";
        }
        if (code === 'CARE_TENANT_SCOPE_DENIED' ||
            (typeof message === 'string' && /tenant.*scope/i.test(message))) {
          return "That resource belongs to a different organization.";
        }
        return message || 'Access denied.';
      })();

      // Surface via toast if a push function was provided. Shape matches
      // notification objects (priority/title/message) so the same
      // ToastContainer render path is reused. `priority: 'urgent'` maps
      // to a 5-second display duration via useNotifications' durationMap.
      if (typeof onToast === 'function') {
        onToast({
          priority: 'urgent',
          title:    'Access denied',
          message:  friendlyMessage,
        });
      } else {
        console.warn('[AccessDenied]', friendlyMessage, {
          code,
          requestUrl,
          requestMethod,
        });
      }

      // Redirect to dashboard unless we're already there. `replace`
      // so the back button doesn't return to the 403'd URL.
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard', { replace: true });
      }
    }

    window.addEventListener('care:access-denied', handleAccessDenied);
    return () => {
      window.removeEventListener('care:access-denied', handleAccessDenied);
    };
  }, [navigate, location.pathname, onToast]);

  return null;
}